import { useCallback, useEffect, useState } from "react";
import type { ShopOperationDetailDto } from "@grizzly/shared";
import { operationStatus } from "@grizzly/shared";
import { api, attachmentContentUrl } from "../../lib/apiClient.js";
import type { ApiClientError } from "../../lib/apiClient.js";
import { formatDateTime } from "../../lib/format.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n, type MessageKey } from "../../i18n/index.js";
import { OperationReportForm } from "./reportForm.js";
import { CheckCircle2, PauseCircle, PlayCircle, Ban, Download } from "lucide-react";

export function OperationPanel({
  operationId,
  canReport,
  onChanged,
}: {
  operationId: string;
  canReport: boolean;
  onChanged(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [detail, setDetail] = useState<ShopOperationDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [blockDesc, setBlockDesc] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getShopOperation(operationId);
      setDetail(res.operation);
      setError(null);
    } catch (err) {
      setError((err as ApiClientError).message);
    }
  }, [operationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = useCallback(
    async (fn: () => Promise<unknown>, success: string) => {
      setBusy(true);
      try {
        await fn();
        push(success);
        setReporting(false);
        setBlocking(false);
        setBlockDesc("");
        await load();
        onChanged();
      } catch (err) {
        const e = err as ApiClientError;
        push(
          e.message,
          e.requestId ? t("common.requestId", { id: e.requestId }) : undefined,
          "danger"
        );
      } finally {
        setBusy(false);
      }
    },
    [load, onChanged, push, t]
  );

  const uploadPhoto = useCallback(async () => {
    if (!photo) return;
    setBusy(true);
    try {
      await api.uploadShopOperationPhoto(operationId, photo);
      push(t("shopFloor.photoAttached"));
      setPhoto(null);
      await load();
    } catch (err) {
      const e = err as ApiClientError;
      push(e.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [load, operationId, photo, push, t]);

  if (error)
    return (
      <p role="alert" className="text-sm text-action-danger">
        {error}
      </p>
    );
  if (!detail) return null;

  const op = detail.operation;
  const status = operationStatus(op.status);

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink">
            {detail.job.number} · {op.sequence + 1}. {op.description}
          </h2>
          <p className="text-xs text-ink-muted">
            {detail.job.title} · {detail.job.customerName ?? t("shopFloor.internal")} ·{" "}
            {t("shopFloor.qty")} {detail.job.quantity}
            {detail.job.dueDate
              ? ` · ${t("shopFloor.dueAt", { date: detail.job.dueDate })}`
              : ""}
          </p>
        </div>
        <Badge tone={status.tone}>
          {t(`status.operation.${op.status}` as MessageKey)}
        </Badge>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {canReport ? (
          <>
            {op.status === "ready" ? (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => api.startShopOperation(operationId),
                    t("shopFloor.operationStarted")
                  )
                }
              >
                <PlayCircle className="h-4 w-4 rtl:-scale-x-100" aria-hidden="true" />{" "}
                {t("shopFloor.start")}
              </Button>
            ) : null}
            {op.status === "active" ? (
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => api.pauseShopOperation(operationId),
                    t("shopFloor.operationPaused")
                  )
                }
              >
                <PauseCircle className="h-4 w-4" aria-hidden="true" />{" "}
                {t("shopFloor.pause")}
              </Button>
            ) : null}
            {op.status === "paused" ? (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => api.resumeShopOperation(operationId),
                    t("shopFloor.operationResumed")
                  )
                }
              >
                <PlayCircle className="h-4 w-4 rtl:-scale-x-100" aria-hidden="true" />{" "}
                {t("shopFloor.resume")}
              </Button>
            ) : null}
            {op.status === "active" ? (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => setReporting(true)}
              >
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />{" "}
                {t("shopFloor.complete")}
              </Button>
            ) : null}
            {op.status === "blocked" ? (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => api.unblockShopOperation(operationId),
                    t("shopFloor.operationUnblocked")
                  )
                }
              >
                {t("shopFloor.unblock")}
              </Button>
            ) : null}
            {["ready", "active", "paused"].includes(op.status) ? (
              <Button variant="danger" disabled={busy} onClick={() => setBlocking(true)}>
                <Ban className="h-4 w-4" aria-hidden="true" /> {t("shopFloor.block")}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>

      {detail.activeTime ? (
        <p className="mt-2 text-xs text-ink-muted">
          {t("shopFloor.startedAt", {
            time: formatDateTime(detail.activeTime.startedAt),
          })}
        </p>
      ) : null}
      {detail.totalMinutes > 0 ? (
        <p className="mt-1 text-xs text-ink-muted">
          {t("shopFloor.totalTime", { minutes: detail.totalMinutes.toFixed(1) })}
        </p>
      ) : null}

      {reporting ? (
        <div className="mt-3">
          <OperationReportForm
            reasons={detail.reasons}
            onCancel={() => setReporting(false)}
            onSubmit={(good, scrap, rework, note) =>
              void act(
                () =>
                  api.completeShopOperation(operationId, { good, scrap, rework, note }),
                t("shopFloor.operationCompleted")
              )
            }
          />
        </div>
      ) : null}

      {blocking ? (
        <div className="mt-3 space-y-2 rounded border border-line p-3">
          <TextInput
            label={t("shopFloor.blockQuestion")}
            value={blockDesc}
            onChange={(e) => setBlockDesc(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              variant="danger"
              disabled={busy || blockDesc.trim().length < 3}
              onClick={() =>
                void act(
                  () => api.blockShopOperation(operationId, blockDesc, "high"),
                  t("shopFloor.operationBlocked")
                )
              }
            >
              {t("shopFloor.blockOperation")}
            </Button>
            <Button variant="ghost" onClick={() => setBlocking(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}

      {canReport ? (
        <div className="mt-3 flex items-end gap-2 border-t border-line pt-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="op-photo" className="text-sm font-medium text-ink">
              {t("shopFloor.attachPhoto")}
            </label>
            <input
              id="op-photo"
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.pdf"
              onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
              className="h-11 rounded-[var(--radius-card)] border border-line bg-surface-raised px-2 py-2 text-sm file:me-2 file:border-0 file:bg-surface-inset file:rounded file:px-3 file:py-1.5"
            />
          </div>
          <Button variant="secondary" disabled={busy || !photo} onClick={uploadPhoto}>
            {t("shopFloor.upload")}
          </Button>
        </div>
      ) : null}

      <div className="mt-3 space-y-2 border-t border-line pt-3 text-sm">
        <p className="font-medium text-ink">{t("shopFloor.operations")}</p>
        <ul className="space-y-1">
          {detail.operations.map((o) => (
            <li key={o.id} className="flex items-center justify-between text-xs">
              <span>
                {o.sequence + 1}. {o.description}
              </span>
              <Badge tone={operationStatus(o.status).tone}>
                {t(`status.operation.${o.status}` as MessageKey)}
              </Badge>
            </li>
          ))}
        </ul>
      </div>

      {detail.materials.length > 0 ? (
        <div className="mt-2 border-t border-line pt-2 text-xs text-ink-muted">
          {detail.materials.map((m) => (
            <p key={m.id}>
              {m.materialSku} · {m.materialName}: {m.plannedQuantity} {m.unitOfMeasure}
            </p>
          ))}
        </div>
      ) : null}

      {detail.documents.length > 0 ? (
        <div className="mt-2 border-t border-line pt-2">
          <p className="mb-1 text-xs font-medium text-ink">{t("shopFloor.documents")}</p>
          <ul className="space-y-1">
            {detail.documents.map((d) => (
              <li key={d.id} className="flex items-center justify-between text-xs">
                <span>{d.originalName}</span>
                <a
                  href={attachmentContentUrl(d.id)}
                  className="flex items-center gap-1 text-action-primary-text hover:underline"
                >
                  <Download className="h-3 w-3" aria-hidden="true" />{" "}
                  {t("shopFloor.download")}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {detail.issues.length > 0 ? (
        <div className="mt-2 border-t border-line pt-2">
          <p className="mb-1 text-xs font-medium text-ink">{t("shopFloor.issues")}</p>
          <ul className="space-y-1">
            {detail.issues.map((i) => (
              <li key={i.id} className="text-xs text-ink-muted">
                {i.description} — {i.status}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {detail.production.length > 0 ? (
        <div className="mt-2 border-t border-line pt-2">
          <p className="mb-1 text-xs font-medium text-ink">{t("shopFloor.reported")}</p>
          {detail.production.map((p) => (
            <p key={p.id} className="text-xs text-ink-muted">
              {t(`status.production.${p.kind}` as MessageKey)}: {p.quantity}
              {p.reasonLabel ? ` (${p.reasonLabel})` : ""}
            </p>
          ))}
        </div>
      ) : null}
    </Card>
  );
}
